import { useRef, useState, useEffect } from "react";
import { Type, PenLine, Eraser } from "lucide-react";

// Captures a typed or drawn signature and returns a PNG data URL via onChange.
export default function SignaturePad({ defaultName = "", onChange }) {
  const [mode, setMode] = useState("typed");
  const [name, setName] = useState(defaultName);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  const emitTyped = (value) => {
    const c = document.createElement("canvas");
    c.width = 520; c.height = 140;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = "#0a1e3f";
    ctx.font = "italic 52px 'Brush Script MT', 'Segoe Script', cursive";
    ctx.textBaseline = "middle";
    ctx.fillText(value || "", 20, 78);
    onChange?.(value ? c.toDataURL("image/png") : "", "typed");
  };

  useEffect(() => { if (mode === "typed") emitTyped(name); /* eslint-disable-next-line */ }, [mode]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0a1e3f";
    ctx.lineTo(p.x, p.y); ctx.stroke(); hasDrawn.current = true;
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (hasDrawn.current) onChange?.(canvasRef.current.toDataURL("image/png"), "drawn"); };
  const clearCanvas = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height); hasDrawn.current = false; onChange?.("", "drawn");
  };
  useEffect(() => { if (mode === "drawn") clearCanvas(); /* eslint-disable-next-line */ }, [mode]);

  return (
    <div data-testid="signature-pad">
      <div className="flex gap-2 mb-3">
        <button type="button" onClick={() => setMode("typed")} data-testid="sig-mode-typed"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${mode === "typed" ? "bg-cobalt text-white" : "bg-slate-100 text-slate-600"}`}><Type className="w-3.5 h-3.5" /> Type</button>
        <button type="button" onClick={() => setMode("drawn")} data-testid="sig-mode-drawn"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${mode === "drawn" ? "bg-cobalt text-white" : "bg-slate-100 text-slate-600"}`}><PenLine className="w-3.5 h-3.5" /> Draw</button>
      </div>
      {mode === "typed" ? (
        <div>
          <input value={name} onChange={(e) => { setName(e.target.value); emitTyped(e.target.value); }} data-testid="sig-typed-input"
            placeholder="Type your full legal name" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-cobalt" />
          <div className="mt-2 h-24 rounded-xl border border-dashed border-slate-200 flex items-center px-4 text-3xl text-navy" style={{ fontFamily: "'Brush Script MT','Segoe Script',cursive", fontStyle: "italic" }}>{name || <span className="text-base text-slate-300 not-italic" style={{ fontFamily: "inherit" }}>Signature preview</span>}</div>
        </div>
      ) : (
        <div>
          <canvas ref={canvasRef} width={520} height={140} data-testid="sig-canvas"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="w-full h-32 rounded-xl border border-slate-200 bg-white touch-none cursor-crosshair" />
          <button type="button" onClick={clearCanvas} className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy"><Eraser className="w-3.5 h-3.5" /> Clear</button>
        </div>
      )}
    </div>
  );
}
